/**
 * Browser-seitige Geometrie-Helfer für den MengenTab-Editor (Live-Recompute
 * während Vertex-Drag + 2-Punkt-Maßstab). Spiegelt die Engine-Konvention
 * (polygonMetricsFromPdfPt / ratioFromTwoPoints) — bewusst hier dupliziert,
 * weil @bidi-handel/takeoff Node-Imports (createRequire) zieht und nicht
 * browser-bundle-fähig ist. Die autoritative Neuberechnung passiert server-
 * seitig beim PATCH; diese Helfer sind nur für die sofortige Anzeige.
 */

const PT_TO_MM_PAPER = 25.4 / 72;

/** Fläche (m²) + Umfang (m) eines Polygon-Rings (PDF-Pt) bei Maßstab 1:ratio. */
export function polygonMetricsPt(
  ring: number[][],
  ratio: number
): { areaM2: number; perimeterM: number } {
  if (!Number.isFinite(ratio) || ratio <= 0) return { areaM2: 0, perimeterM: 0 };
  const pts = dropClosingDuplicate(ring);
  const n = pts.length;
  if (n < 2) return { areaM2: 0, perimeterM: 0 };
  let area2 = 0;
  let perim = 0;
  for (let i = 0; i < n; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % n]!;
    const x1 = a[0]!;
    const y1 = a[1]!;
    const x2 = b[0]!;
    const y2 = b[1]!;
    area2 += x1 * y2 - x2 * y1;
    perim += Math.hypot(x2 - x1, y2 - y1);
  }
  const ptSqToM2 = Math.pow((PT_TO_MM_PAPER * ratio) / 1000, 2);
  return {
    areaM2: n < 3 ? 0 : (Math.abs(area2) / 2) * ptSqToM2,
    perimeterM: (perim * PT_TO_MM_PAPER * ratio) / 1000,
  };
}

/** Maßstab 1:N aus 2 Punkten (PDF-Pt) + realer Distanz (m). NaN bei Entartung. */
export function ratioFromTwoPointsPt(
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  realMeters: number
): number {
  const d = Math.hypot(p2.x - p1.x, p2.y - p1.y);
  if (d <= 0 || !Number.isFinite(realMeters) || realMeters <= 0) return NaN;
  return (realMeters * 1000) / (d * PT_TO_MM_PAPER);
}

/**
 * Verdachts-Heuristik für Topologie-Artefakte: Umfang/√Fläche. Kompakte Räume
 * liegen bei ~4 (Quadrat) bis ~6 (länglich); Compound-Faces der Topologie-
 * Engine schießen auf 15..100. Schwelle 12 flaggt klare Ausreißer für die UI.
 */
export function isSuspiciousShape(areaM2: number, perimeterM: number): boolean {
  if (areaM2 <= 0) return perimeterM > 0;
  return perimeterM / Math.sqrt(areaM2) > 12;
}

function dropClosingDuplicate(ring: number[][]): number[][] {
  if (ring.length >= 2) {
    const a = ring[0]!;
    const b = ring[ring.length - 1]!;
    if (
      a.length >= 2 &&
      b.length >= 2 &&
      Math.abs(a[0]! - b[0]!) < 1e-9 &&
      Math.abs(a[1]! - b[1]!) < 1e-9
    ) {
      return ring.slice(0, -1);
    }
  }
  return ring;
}
